/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr, enUS, zhCN, arSA } from 'date-fns/locale';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  setStartDate,
  setEndDate,
  nextStep,
  setStepVerified,
} from '../santeMobiliteSlice';
import { startOfTomorrow } from '../utils';

/**
 * Style "input arrondi" cohérent avec RC/Habitation. On le passe au DatePicker
 * via slotProps.textField.sx pour qu'il s'applique au TextField intégré sans
 * casser le typage strict du x-date-pickers.
 */
const ROUND_INPUT_SX: SxProps<Theme> = {
  '& .MuiInputBase-root': { borderRadius: '30px' },
  '& input': { padding: '12px 16px' },
  '& .MuiInputLabel-root[data-shrink="false"]': {
    transform: 'translate(14px, 14px) scale(1)',
  },
};

const addDays = (d: Date, days: number) => {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
};

/**
 * Dernier jour permis pour la date de fin si on souhaite EXACTEMENT 1 an de
 * couverture maximum (= 12 mois).
 *
 * Exemples :
 *   - start = 26/06/2026 → maxEnd = 25/06/2027 (365 jours de couverture,
 *     12 mois pile selon la formule `ceil((diffDays + 1) / 30.4375)`)
 *   - start = 25/07/2026 → maxEnd = 24/07/2027 (idem, 365 jours / 12 mois)
 *
 * Si on autorisait l'anniversaire, on aurait 366 jours de couverture (end
 * inclusif), soit ceil(366 / 30.4375) = 13 mois → contrat invalide côté back
 * dont le catalogue tarifaire s'arrête à 12 mois.
 */
const maxEndDateForOneYear = (start: Date): Date => {
  const r = new Date(start);
  r.setFullYear(r.getFullYear() + 1);
  r.setDate(r.getDate() - 1);
  return r;
};

/** Une date de départ antérieure à demain est refusée. */
const isStartDateTooEarly = (isoStr: string | null) => {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return true;
  return d < startOfTomorrow();
};

/**
 * Toujours minuit UTC, pour éviter les écarts de fuseau (cf. STU-27).
 *
 * Une branche « si c'est aujourd'hui, garder l'heure courante » vivait ici,
 * pour couvrir le jour même. Elle est retirée avec l'interdiction du départ
 * le jour même : ni le début (demain au plus tôt) ni la fin (postérieure au
 * début) ne peuvent plus tomber aujourd'hui.
 */
const toIsoFromDate = (d: Date) =>
  new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0),
  ).toISOString();

const parseIsoToDate = (isoStr: string | null): Date | null => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Décide si une date de fin candidate dépasse la limite "1 an civil" depuis le
 * début. On compare jour-mois plutôt que `diffDays > 365` pour éviter le piège
 * "30 juin 2026 → 30 juin 2027 = 365 jours en année bissextile, mais 12 mois
 * civils nets". Le back applique `ceil(diffDays / 30.4375)` donc 365j retombe
 * sur 12 mois pile, ce qui correspond à 1 an civil. On laisse le cap à
 * +1 an glissant et on toastr au-delà.
 */
const isMoreThanOneYear = (start: Date, end: Date): boolean => {
  return end.getTime() > maxEndDateForOneYear(start).getTime();
};

/**
 * Map des locales date-fns supportées par le DatePicker. On s'aligne sur la
 * locale i18n active de l'app, avec fallback sur fr pour les langues non
 * couvertes par date-fns.
 */
const DATE_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
  zh: zhCN,
  ar: arSA,
};

const ContractDatesStep = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('sante-mobilite');
  const { startDate, endDate } = useAppSelector(state => state.santeMobilite);

  const dateLocale =
    DATE_LOCALES[i18n.language?.split('-')[0] || 'fr'] || fr;

  // Purge des dates périmées si l'utilisateur revient plus tard, ET
  // pré-remplit la date de début avec DEMAIN — la première valeur acceptable —
  // pour fluidifier la saisie (le user peut la reculer s'il part plus tard).
  useEffect(() => {
    if (isStartDateTooEarly(startDate)) {
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
    }
    if (!startDate) {
      dispatch(setStartDate(toIsoFromDate(startOfTomorrow())));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startParsed = parseIsoToDate(startDate);
  const endParsed = parseIsoToDate(endDate);

  const handleStartChange = (newDate: Date | null) => {
    if (!newDate || isNaN(newDate.getTime())) {
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
      return;
    }
    // Si l'utilisateur tape une année à 1 ou 2 chiffres pendant la saisie
    // (eg. "0001-01-01"), le DatePicker le notifie quand même. On ignore
    // tant que l'année n'est pas réaliste, pour ne pas casser la frappe.
    if (newDate.getFullYear() < 1000) return;
    dispatch(setStartDate(toIsoFromDate(newDate)));
  };

  const handleEndChange = (newDate: Date | null) => {
    if (!newDate || isNaN(newDate.getTime())) {
      dispatch(setEndDate(null));
      return;
    }
    if (newDate.getFullYear() < 1000) return;
    dispatch(setEndDate(toIsoFromDate(newDate)));
  };

  const isValid = !!startDate && !!endDate;

  /**
   * Toute la validation est concentrée ici. Si quelque chose cloche on affiche
   * un toastr et on bloque la transition vers le step suivant — mais on ne
   * touche pas aux dates saisies pour que l'utilisateur puisse corriger.
   */
  const handleNext = () => {
    if (!isValid) return;

    if (isStartDateTooEarly(startDate)) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step1.toastr_start_past'),
      );
      return;
    }

    const start = new Date(startDate as string);
    const end = new Date(endDate as string);
    if (end <= start) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step1.toastr_end_before_start'),
      );
      return;
    }

    // Le contrat couvre au maximum 1 an civil (même jour-mois l'année
    // suivante). Cap aligné sur la formule monthsCount du back :
    // ceil(diffDays / 30.4375) ≤ 12.
    if (isMoreThanOneYear(start, end)) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step1.toastr_max_one_year'),
      );
      return;
    }

    dispatch(setStepVerified(1));
    dispatch(nextStep());
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <Box sx={{ maxWidth: 500, mx: 'auto', p: 2 }}>
        <Typography variant="subtitle1" gutterBottom mb={0}>
          {t('step1.title')}
        </Typography>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12}>
            <DatePicker
              label={t('step1.start_date')}
              value={startParsed}
              onChange={handleStartChange}
              // `minDate` et non `disablePast` : ce dernier n'exclut que les
              // jours révolus et laissait donc choisir aujourd'hui.
              minDate={startOfTomorrow()}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: ROUND_INPUT_SX,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              label={t('step1.end_date')}
              value={endParsed}
              onChange={handleEndChange}
              disabled={!startParsed}
              format="dd/MM/yyyy"
              minDate={startParsed ? addDays(startParsed, 1) : undefined}
              maxDate={
                startParsed ? maxEndDateForOneYear(startParsed) : undefined
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: ROUND_INPUT_SX,
                },
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'gray' }}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="body2">{t('step1.info')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button onClick={handleNext} disabled={!isValid} variant="contained">
            {t('step1.next')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ContractDatesStep;
